import { useEffect, useState, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight, ChevronDown, Search, X, Filter, Boxes, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { getCtes, getCtesExportUrl, formatDateTime, formatDate } from "@/lib/ocorenApi";
import { ESTAGIOS_CTE, getEstagioCte } from "@/lib/estagiosCte";
import { CteNfsList } from "@/components/CteNfsList";
import { OcorrenciaDrawer } from "@/components/OcorrenciaDrawer";
import { CteActions } from "@/components/CteActions";

import { useTransportadora } from "@/contexts/TransportadoraContext";

const EMPTY_FILTERS = {
  numero_cte: "",
  numero_nota: "",
  estagio: "",
  cnpj_transportadora: "",
};

const PAGE_SIZE = 30;

const Field = ({ label, testId, value, onChange, placeholder, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
      {label}
    </label>
    {children ? (
      children
    ) : (
      <input
        data-testid={testId}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B] placeholder:text-[#A1A1AA]"
      />
    )}
  </div>
);

const EstagioBadge = ({ estagioKey }) => {
  const est = getEstagioCte(estagioKey);
  const Icon = est.icon;
  return (
    <span
      data-testid={`cte-estagio-${estagioKey}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${est.bg} ${est.text} ${est.border}`}
    >
      <Icon size={11} />
      {est.label}
    </span>
  );
};

const ResumoCard = ({ estagio, total, selected, onClick }) => {
  const Icon = estagio.icon;
  return (
    <button
      data-testid={`cte-resumo-${estagio.key}`}
      onClick={onClick}
      className={`text-left bg-white border rounded-md p-4 transition-colors ${
        selected ? "border-[#09090B] ring-1 ring-[#09090B]" : "border-[#E4E4E7] hover:border-[#A1A1AA]"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-sm ${estagio.bg} ${estagio.text}`}>
          <Icon size={14} />
        </span>
      </div>
      <div className="text-2xl font-bold font-mono-data text-[#09090B]">{total}</div>
      <div className="text-xs text-[#52525B] mt-0.5">{estagio.label}</div>
    </button>
  );
};

export default function Ctes() {
  const { transportadoras } = useTransportadora();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialEstagio = searchParams.get("estagio") || "";

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, estagio: initialEstagio });
  const [data, setData] = useState({ total: 0, items: [], resumo_estagios: {} });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [skip, setSkip] = useState(0);
  const [aviso, setAviso] = useState(null);

  // Filtra transportadoras por raiz do CNPJ
  const uniqueTransportadoras = [];
  const seenRaiz = new Set();
  for (const t of transportadoras) {
    const raiz = (t.cnpj || "").slice(0, 8);
    if (raiz && !seenRaiz.has(raiz)) {
      seenRaiz.add(raiz);
      uniqueTransportadoras.push(t);
    }
  }
  const [selectedMov, setSelectedMov] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const showTimeline = (mov) => {
    setSelectedMov(mov);
    setDrawerOpen(true);
  };

  const fetchData = useCallback(async (f, skipVal) => {
    setLoading(true);
    try {
      const res = await getCtes({ ...f, skip: skipVal, limit: PAGE_SIZE });
      setData(res);
      setAviso(res.aviso || null);
      if ((res.transportadoras_indisponiveis || []).length > 0) {
        toast.warning(
          `${res.transportadoras_indisponiveis.length} transportadora(s) sem resposta do serviço de vínculo CT-e no momento.`
        );
      }
    } catch {
      toast.error("Falha ao carregar CT-es (verifique a conexão com o serviço de vínculo).");
    } finally {
      setLoading(false);
    }
  }, []);

  const reload = () => fetchData(filters, skip);

  useEffect(() => {
    fetchData(filters, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (k) => (e) => setFilters((prev) => ({ ...prev, [k]: e.target.value }));

  const applyFilters = () => {
    setSkip(0);
    fetchData(filters, 0);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSkip(0);
    setSearchParams({});
    fetchData(EMPTY_FILTERS, 0);
  };

  const onSelectEstagio = (key) => {
    const next = { ...filters, estagio: filters.estagio === key ? "" : key };
    setFilters(next);
    setSkip(0);
    if (next.estagio) setSearchParams({ estagio: next.estagio });
    else setSearchParams({});
    fetchData(next, 0);
  };

  const goToPage = (dir) => {
    const next = Math.max(0, skip + dir * PAGE_SIZE);
    setSkip(next);
    fetchData(filters, next);
  };

  const resumo = data.resumo_estagios || {};

  return (
    <div data-testid="page-ctes" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Visão por CT-e
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight flex items-center gap-3">
            <Boxes size={32} strokeWidth={2} />
            Conhecimento de Transporte
          </h1>
          <p className="text-sm text-[#52525B] mt-2">
            {data.total} CT-e(s) — vínculo consultado via serviço de vínculo CT-e↔NF-e; o
            estágio de entrega é calculado a partir do status das NFs no nosso sistema.
          </p>
        </div>
        <a
          data-testid="ctes-export-btn"
          href={getCtesExportUrl(filters)}
          className="inline-flex items-center gap-2 bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-4 py-2 text-sm font-medium rounded-md transition-colors"
        >
          <Download size={14} />
          Exportar Excel
        </a>
      </div>

      {aviso && (
        <div
          data-testid="cte-aviso"
          className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md p-4 flex items-start gap-2"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          {aviso}
        </div>
      )}

      {/* Resumo por estágio */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {ESTAGIOS_CTE.map((est) => (
          <ResumoCard
            key={est.key}
            estagio={est}
            total={resumo[est.key] || 0}
            selected={filters.estagio === est.key}
            onClick={() => onSelectEstagio(est.key)}
          />
        ))}
      </div>

      {/* Filtros */}
      <div
        data-testid="ctes-filters"
        className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Filter size={14} className="text-[#52525B]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
            Filtros
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Field
            label="Número CT-e"
            testId="ctes-filter-numero"
            value={filters.numero_cte}
            onChange={onChange("numero_cte")}
            placeholder="Ex: 4995366"
          />
          <Field
            label="Número NF"
            testId="ctes-filter-nf"
            value={filters.numero_nota}
            onChange={onChange("numero_nota")}
            placeholder="Buscar CT-e que contém esta NF"
          />
          <Field label="Estágio">
            <select
              data-testid="ctes-filter-estagio"
              value={filters.estagio}
              onChange={onChange("estagio")}
              className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
            >
              <option value="">Todos</option>
              {ESTAGIOS_CTE.map((e) => (
                <option key={e.key} value={e.key}>
                  {e.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Transportadora">
            <select
              data-testid="ctes-filter-transportadora"
              value={filters.cnpj_transportadora}
              onChange={onChange("cnpj_transportadora")}
              className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
            >
              <option value="">Todas</option>
              {uniqueTransportadoras.map((t) => (
                <option key={t.cnpj} value={t.cnpj}>
                  {t.nome} ({(t.cnpj || "").slice(0, 8)})
                </option>
              ))}
            </select>
          </Field>
          <div className="flex items-end gap-2">
            <button
              data-testid="ctes-apply-btn"
              onClick={applyFilters}
              className="flex-1 bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors"
            >
              <Search size={14} />
              Filtrar
            </button>
            <button
              data-testid="ctes-clear-btn"
              onClick={clearFilters}
              className="bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-3 py-2 text-sm font-medium rounded-md transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabela (Desktop/Tablet) */}
      <div className="hidden md:block bg-white border border-[#E4E4E7] rounded-md overflow-x-auto">
        <table className="data-table w-full min-w-[760px] text-sm" data-testid="ctes-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>CT-e</th>
              <th>Emissão</th>
              <th>Previsão entrega</th>
              <th className="text-center">Qtd. NFs</th>
              <th className="text-center">NFs entregues</th>
              <th>Estágio</th>
              <th>Transportadora (CNPJ)</th>
              <th className="w-12 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-center text-[#52525B] py-10">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-[#52525B] py-10">
                  Nenhum CT-e encontrado com os filtros atuais.
                </td>
              </tr>
            )}
            {!loading &&
              data.items.map((c, idx) => {
                const key = `${c.cte_numero}-${c.cnpj_transportadora}-${idx}`;
                const isExpanded = expanded === key;
                return (
                  <Fragment key={key}>
                    <tr
                      data-testid={`cte-row-${idx}`}
                      onClick={() => setExpanded(isExpanded ? null : key)}
                      className="cursor-pointer"
                    >
                      <td className="w-8 text-[#52525B]">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="font-mono-data font-semibold text-[#09090B]">
                        {c.cte_numero}
                      </td>
                      <td className="text-xs text-[#52525B] font-mono-data whitespace-nowrap">
                        {formatDateTime(c.cte_dtemissao)}
                      </td>
                      <td className="text-xs text-[#52525B] font-mono-data whitespace-nowrap">
                        {c.dprev_entrega ? formatDate(c.dprev_entrega) : "—"}
                      </td>
                      <td className="text-center">
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 text-xs font-bold font-mono-data bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm">
                          {c.qtd_nfs}
                        </span>
                      </td>
                      <td className="text-center text-xs font-mono-data text-[#52525B]">
                        {c.nfs_entregues}/{c.qtd_nfs}
                      </td>
                      <td>
                        <EstagioBadge estagioKey={c.estagio} />
                      </td>
                      <td className="text-xs text-[#52525B] font-mono-data">
                        {c.cnpj_transportadora}
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <CteActions cte={c} onChanged={reload} idx={idx} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${key}-expanded`}>
                        <td colSpan={9} className="bg-[#FAFAFA] p-6">
                          <CteNfsList
                            numeroCte={c.cte_numero}
                            cnpjTransportadora={c.cnpj_transportadora}
                            onShowTimeline={showTimeline}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Cards (Mobile) */}
      <div className="block md:hidden space-y-4">
        {loading && (
          <div className="text-center text-[#52525B] py-10 bg-white border border-[#E4E4E7] rounded-md">
            Carregando…
          </div>
        )}
        {!loading && data.items.length === 0 && (
          <div className="text-center text-[#52525B] py-10 bg-white border border-[#E4E4E7] rounded-md">
            Nenhum CT-e encontrado com os filtros atuais.
          </div>
        )}
        {!loading &&
          data.items.map((c, idx) => {
            const key = `${c.cte_numero}-${c.cnpj_transportadora}-${idx}`;
            const isExpanded = expanded === key;
            return (
              <div
                key={key}
                data-testid={`cte-card-${idx}`}
                className="bg-white border border-[#E4E4E7] rounded-md p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExpanded(isExpanded ? null : key)}
                      className="p-1 text-[#52525B] hover:bg-gray-100 rounded"
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <div>
                      <div className="font-mono-data font-semibold text-[#09090B]">
                        CT-e {c.cte_numero}
                      </div>
                      <div className="text-xs text-[#52525B] font-mono-data mt-0.5">
                        {formatDateTime(c.cte_dtemissao)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <EstagioBadge estagioKey={c.estagio} />
                    <CteActions cte={c} onChanged={reload} idx={idx} />
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-[#F4F4F5] pt-2 text-xs">
                  <div className="text-[#52525B]">
                    NFs: <span className="font-semibold text-[#09090B]">{c.qtd_nfs}</span> · Entregues:{" "}
                    <span className="font-semibold text-[#09090B]">
                      {c.nfs_entregues}/{c.qtd_nfs}
                    </span>
                  </div>
                  <div className="text-[#52525B] font-mono-data">
                    Prev.: {c.dprev_entrega ? formatDate(c.dprev_entrega) : "—"}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-[#F4F4F5] pt-3 mt-3 bg-[#FAFAFA] -mx-4 -mb-4 p-4 rounded-b-md">
                    <CteNfsList
                      numeroCte={c.cte_numero}
                      cnpjTransportadora={c.cnpj_transportadora}
                      onShowTimeline={showTimeline}
                    />
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Paginação */}
      {!loading && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm text-[#52525B]">
          <span>
            Mostrando {skip + 1}–{Math.min(skip + PAGE_SIZE, data.total)} de {data.total}
          </span>
          <div className="flex gap-2">
            <button
              data-testid="ctes-prev-page"
              disabled={skip === 0}
              onClick={() => goToPage(-1)}
              className="px-3 py-1.5 border border-[#E4E4E7] rounded-md disabled:opacity-40 hover:border-[#09090B]"
            >
              Anterior
            </button>
            <button
              data-testid="ctes-next-page"
              disabled={skip + PAGE_SIZE >= data.total}
              onClick={() => goToPage(1)}
              className="px-3 py-1.5 border border-[#E4E4E7] rounded-md disabled:opacity-40 hover:border-[#09090B]"
            >
              Próximo
            </button>
          </div>
        </div>
      )}

      <OcorrenciaDrawer
        mov={selectedMov}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}
